import Navigation from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { BarChart3, TrendingUp, TrendingDown, Search, Loader2 } from "lucide-react";
import { useState } from "react";
import { soundManager } from "@/lib/soundManager";

export default function Statistics() {
  const { data: analysis, isLoading } = trpc.prediction.getAnalysis.useQuery({ digitType: 4 });
  const { data: frequentNumbers, isLoading: isLoadingFrequent } = trpc.lottery.getMostFrequentNumbers.useQuery({ limit: 20 });
  
  const [searchNumber, setSearchNumber] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAnimatingSearch, setIsAnimatingSearch] = useState(false);
  const { data: searchResult, isLoading: isSearching } = trpc.lottery.searchNumber.useQuery(
    { number: searchQuery },
    { enabled: searchQuery.length >= 2 && searchQuery.length <= 4 && /^\d+$/.test(searchQuery) }
  );

  const handleSearch = () => {
    if (searchNumber.length >= 2 && searchNumber.length <= 4 && /^\d+$/.test(searchNumber)) {
      // Play search sound and start animation
      soundManager.playPrediction();
      setIsAnimatingSearch(true);
      
      // Artificial delay for better UX (1.5 seconds)
      setTimeout(() => {
        setSearchQuery(searchNumber);
        // Keep animation for another 0.5s after query starts
        setTimeout(() => {
          setIsAnimatingSearch(false);
          if (searchResult) {
            soundManager.playSuccess();
          }
        }, 500);
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
      <Navigation />
      <main className="flex-1 pb-20 md:pb-8">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-primary via-amber-300 to-primary bg-clip-text text-transparent">
              Statistical Analysis
            </h1>
            <p className="text-muted-foreground">
              Comprehensive insights from historical lottery data
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : analysis ? (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-gradient-to-br from-card to-blue-950/20">
                  <CardHeader>
                    <CardTitle className="text-primary">Total Draws</CardTitle>
                    <CardDescription>Analyzed</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold">{analysis.totalDraws.toLocaleString()}</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-card to-blue-950/20">
                  <CardHeader>
                    <CardTitle className="text-primary">Date Range</CardTitle>
                    <CardDescription>Coverage</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg font-semibold">{analysis.dateRange.from}</p>
                    <p className="text-sm text-muted-foreground">to {analysis.dateRange.to}</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-card to-blue-950/20">
                  <CardHeader>
                    <CardTitle className="text-primary">Even/Odd Ratio</CardTitle>
                    <CardDescription>Distribution</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {(analysis.analysis.evenOddRatio.even * 100).toFixed(1)}% / {(analysis.analysis.evenOddRatio.odd * 100).toFixed(1)}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Number Search */}
              <Card className="bg-gradient-to-br from-card to-blue-950/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="w-5 h-5 text-primary" />
                    Search Number
                  </CardTitle>
                  <CardDescription>
                    Search for any 2-digit, 3-digit, or 4-digit number to see how many times it appeared in lottery history
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3 mb-6">
                    <Input
                      type="text"
                      placeholder="Enter 2-4 digit number (e.g., 1234)"
                      value={searchNumber}
                      onChange={(e) => setSearchNumber(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      className="max-w-md"
                    />
                    <Button 
                      onClick={() => {
                        soundManager.playClick();
                        handleSearch();
                      }} 
                      disabled={searchNumber.length < 2 || searchNumber.length > 4 || isAnimatingSearch}
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>

                  {(isSearching || isAnimatingSearch) && (
                    <div className="flex flex-col items-center justify-center py-8 space-y-4">
                      <Loader2 className="w-12 h-12 animate-spin text-primary" />
                      <div className="text-center">
                        <p className="text-lg font-semibold text-primary animate-pulse">Searching lottery history...</p>
                        <p className="text-sm text-muted-foreground mt-1">Analyzing {searchNumber} across all draws</p>
                      </div>
                    </div>
                  )}

                  {searchResult && !isSearching && (
                    <div className="space-y-4">
                      {searchResult.found ? (
                        <>
                          <div className="p-6 rounded-lg bg-gradient-to-br from-primary/20 to-amber-500/20 border border-primary/30">
                            <div className="flex items-center justify-between mb-4">
                              <div className="text-sm font-semibold text-muted-foreground">Search Result</div>
                              <div className="text-sm font-semibold text-primary">{searchResult.totalCount}x appearances</div>
                            </div>
                            <div className="text-5xl font-bold text-primary font-mono text-center mb-6">
                              {searchResult.number}
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div className="p-4 rounded-lg bg-background/50">
                                <div className="text-sm text-muted-foreground mb-2">1st Prize</div>
                                <div className="text-3xl font-bold text-amber-300">{searchResult.positions.first}</div>
                              </div>
                              <div className="p-4 rounded-lg bg-background/50">
                                <div className="text-sm text-muted-foreground mb-2">2nd Prize</div>
                                <div className="text-3xl font-bold text-blue-300">{searchResult.positions.second}</div>
                              </div>
                              <div className="p-4 rounded-lg bg-background/50">
                                <div className="text-sm text-muted-foreground mb-2">3rd Prize</div>
                                <div className="text-3xl font-bold text-purple-300">{searchResult.positions.third}</div>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center mt-4">
                              Searched across {searchResult.totalDrawsSearched.toLocaleString()} historical draws
                            </div>
                          </div>

                          {searchResult.occurrences && searchResult.occurrences.length > 0 && (
                            <div className="p-6 rounded-lg bg-card border border-border">
                              <h3 className="text-lg font-semibold mb-4 text-primary">Historical Occurrences ({searchResult.occurrences.length})</h3>
                              <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
                                {searchResult.occurrences.map((occurrence, idx) => (
                                  <div
                                    key={idx}
                                    className="p-4 rounded-lg bg-gradient-to-br from-secondary/50 to-secondary/30 border border-border hover:border-primary/50 transition-colors"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-sm font-semibold text-primary">
                                        {new Date(occurrence.date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className={`text-xs font-semibold px-2 py-1 rounded ${occurrence.position === '1st' ? 'bg-amber-500/20 text-amber-300' : occurrence.position === '2nd' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'}`}>
                                        {occurrence.position} Prize
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm font-mono">
                                      <span className="text-amber-300">{occurrence.firstPrize}</span>
                                      <span className="text-muted-foreground">•</span>
                                      <span className="text-blue-300">{occurrence.secondPrize}</span>
                                      <span className="text-muted-foreground">•</span>
                                      <span className="text-purple-300">{occurrence.thirdPrize}</span>
                                      <span className="text-muted-foreground">•</span>
                                      <span className="text-xs text-muted-foreground">{occurrence.color}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="p-8 rounded-lg bg-secondary/50 border border-border text-center">
                          <div className="text-4xl font-bold text-muted-foreground mb-2">{searchResult.number}</div>
                          <p className="text-muted-foreground">This number has not appeared in any lottery draws yet.</p>
                          <div className="text-xs text-muted-foreground mt-2">
                            Searched across {searchResult.totalDrawsSearched.toLocaleString()} historical draws
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Most Frequent 4-Digit Numbers */}
              {!isLoadingFrequent && frequentNumbers && (
                <Card className="bg-gradient-to-br from-card to-blue-950/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Most Frequent 4-Digit Numbers
                    </CardTitle>
                    <CardDescription>
                      Top 20 most frequently drawn numbers across all prize positions ({frequentNumbers.totalDrawsAnalyzed.toLocaleString()} draws analyzed)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {frequentNumbers.mostFrequent.map((item, i) => (
                        <div
                          key={i}
                          className="p-4 rounded-lg bg-gradient-to-br from-primary/20 to-amber-500/20 border border-primary/30"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold text-muted-foreground">#{i + 1}</div>
                            <div className="text-xs font-semibold text-primary">{item.count}x</div>
                          </div>
                          <div className="text-3xl font-bold text-primary font-mono text-center mb-3">
                            {item.number}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="text-center">
                              <div className="text-muted-foreground">1st</div>
                              <div className="font-semibold text-amber-300">{item.positions.first}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-muted-foreground">2nd</div>
                              <div className="font-semibold text-blue-300">{item.positions.second}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-muted-foreground">3rd</div>
                              <div className="font-semibold text-purple-300">{item.positions.third}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Hot Numbers */}
              <Card className="bg-gradient-to-br from-card to-blue-950/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Hot Numbers
                  </CardTitle>
                  <CardDescription>Most frequently appearing 4-digit numbers</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {analysis.analysis.hotNumbers.slice(0, 20).map((num, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-lg bg-gradient-to-br from-primary/20 to-amber-500/20 border border-primary/30 text-center"
                      >
                        <div className="text-xs text-muted-foreground mb-1">#{i + 1}</div>
                        <div className="text-xl font-bold text-primary font-mono">{num}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Cold Numbers */}
              <Card className="bg-gradient-to-br from-card to-blue-950/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-blue-400" />
                    Cold Numbers
                  </CardTitle>
                  <CardDescription>Least frequently appearing 4-digit numbers</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {analysis.analysis.coldNumbers.slice(0, 20).map((num, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 text-center"
                      >
                        <div className="text-xs text-muted-foreground mb-1">#{i + 1}</div>
                        <div className="text-xl font-bold text-blue-400 font-mono">{num}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Digit Frequency */}
              <Card className="bg-gradient-to-br from-card to-blue-950/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Digit Frequency (0-9)
                  </CardTitle>
                  <CardDescription>How often each digit appears across all positions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-4">
                    {Object.entries(analysis.analysis.frequencyAnalysis)
                      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                      .map(([digit, count]) => {
                        const maxCount = Math.max(...Object.values(analysis.analysis.frequencyAnalysis));
                        const percentage = (count / maxCount) * 100;
                        return (
                          <div key={digit} className="text-center">
                            <div className="text-2xl font-bold text-primary mb-2">{digit}</div>
                            <div className="h-24 bg-secondary rounded-lg overflow-hidden flex flex-col justify-end">
                              <div
                                className="bg-gradient-to-t from-primary to-amber-300 transition-all"
                                style={{ height: `${percentage}%` }}
                              />
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">{count}</div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>

              {/* Color Patterns */}
              <Card className="bg-gradient-to-br from-card to-blue-950/20">
                <CardHeader>
                  <CardTitle>Color Distribution</CardTitle>
                  <CardDescription>Frequency of colors in lottery draws</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(analysis.analysis.colorPatterns)
                      .sort((a, b) => b[1] - a[1])
                      .map(([color, count]) => (
                        <div key={color} className="p-4 rounded-lg bg-secondary/50 border border-border">
                          <div className="text-sm text-muted-foreground mb-1">{color}</div>
                          <div className="text-2xl font-bold text-primary">{count}</div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>No data available. Please scrape historical data first.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
